import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { transactionService } from '../../../services/transactionService';

const TransactionModal = ({ isOpen, onClose, wallets, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('send');
  const [selectedChain, setSelectedChain] = useState('ethereum');
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [asset, setAsset] = useState('ETH');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [estimatedFee, setEstimatedFee] = useState(null);

  if (!isOpen) return null;

  const handleSend = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      let result;
      if (selectedChain === 'ethereum') {
        result = await transactionService?.sendEthereum(
          wallets?.ethereum,
          recipient,
          amount
        );
      } else {
        result = await transactionService?.sendSolana(
          wallets?.solana,
          recipient,
          amount
        );
      }

      if (result?.success) {
        onSuccess?.(result);
        onClose();
      } else {
        setError(result?.error || 'Transaction failed');
      }
    } catch (err) {
      setError(err?.message || 'Transaction failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEstimateFee = async () => {
    if (recipient && amount && selectedChain === 'ethereum') {
      const fee = await transactionService?.estimateEthereumFee(recipient, amount);
      setEstimatedFee(fee);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Transaction</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {['send', 'receive', 'swap']?.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-smooth capitalize ${
                  activeTab === tab
                    ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'send' && (
            <div className="space-y-4">
              <Select
                label="Chain"
                value={selectedChain}
                onChange={(e) => setSelectedChain(e?.target?.value)}
              >
                <option value="ethereum">Ethereum</option>
                <option value="solana">Solana</option>
              </Select>

              <Select
                label="Asset"
                value={asset}
                onChange={(e) => setAsset(e?.target?.value)}
              >
                <option value="ETH">ETH</option>
                <option value="USDC">USDC</option>
                <option value="USDT">USDT</option>
              </Select>

              <Input
                label="Recipient Address"
                placeholder="0x..."
                value={recipient}
                onChange={(e) => setRecipient(e?.target?.value)}
                onBlur={handleEstimateFee}
              />

              <Input
                label="Amount"
                type="number"
                placeholder="0.0"
                value={amount}
                onChange={(e) => setAmount(e?.target?.value)}
                onBlur={handleEstimateFee}
              />

              {estimatedFee && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Estimated Fee</p>
                  <p className="text-sm font-semibold text-foreground">
                    {estimatedFee?.totalFee} {estimatedFee?.currency}
                  </p>
                </div>
              )}

              {error && (
                <div className="p-3 bg-error/10 border border-error rounded-lg">
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleSend}
                disabled={!recipient || !amount || isProcessing}
                iconName={isProcessing ? 'Loader' : 'Send'}
              >
                {isProcessing ? 'Processing...' : 'Send'}
              </Button>
            </div>
          )}

          {activeTab === 'receive' && (
            <div className="space-y-4">
              <Select
                label="Chain"
                value={selectedChain}
                onChange={(e) => setSelectedChain(e?.target?.value)}
              >
                <option value="ethereum">Ethereum</option>
                <option value="solana">Solana</option>
              </Select>

              <div className="p-6 bg-muted/30 rounded-lg text-center">
                <div className="w-48 h-48 mx-auto bg-white p-4 rounded-lg mb-4">
                  <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                    <Icon name="QrCode" size={120} className="text-gray-400" />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-2">Your {selectedChain} address</p>
                <p className="text-sm font-mono bg-muted p-3 rounded break-all">
                  {selectedChain === 'ethereum' ? wallets?.ethereum : wallets?.solana}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Copy"
                  className="mt-4"
                  onClick={() => {
                    navigator.clipboard?.writeText(
                      selectedChain === 'ethereum' ? wallets?.ethereum : wallets?.solana
                    );
                  }}
                >
                  Copy Address
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'swap' && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">From</p>
                <div className="flex items-center gap-3">
                  <Input type="number" placeholder="0.0" className="flex-1" />
                  <Select className="w-32">
                    <option value="ETH">ETH</option>
                    <option value="USDC">USDC</option>
                  </Select>
                </div>
              </div>

              <div className="flex justify-center">
                <button className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Icon name="ArrowDownUp" size={20} className="text-primary" />
                </button>
              </div>

              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">To</p>
                <div className="flex items-center gap-3">
                  <Input type="number" placeholder="0.0" className="flex-1" disabled />
                  <Select className="w-32">
                    <option value="USDC">USDC</option>
                    <option value="ETH">ETH</option>
                  </Select>
                </div>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Rate</span>
                  <span className="text-foreground font-medium">1 ETH = 2,420 USDC</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Fee</span>
                  <span className="text-foreground font-medium">0.3%</span>
                </div>
              </div>

              <Button variant="primary" size="lg" fullWidth iconName="RefreshCw">
                Swap
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;
